// Generated by scripts/generate-help.mjs from the Markdown topics in OAA-BI-FE/Help.
// Do not edit: change the Markdown and run `npm run generate:help`.
import type { HelpBrief } from '../model/types';

/**
 * Help bulb previews by help URL (small, always loaded): every topic with its summary, and every
 * `##`/`###` heading of the screen topics (`/help/screens/overview#mrr-trend`) with its first paragraph.
 */
export const HELP_BRIEFS: Readonly<Record<string, HelpBrief>> = {
  '/help/screens/navigation': {
    title: 'Navigation and sidebar',
    text: 'The left menu with the report pages, saved views and their counts, Help, your profile and the data sources',
  },
  '/help/screens/navigation#report-pages': {
    title: 'Report pages',
    page: 'Navigation and sidebar',
    text: 'Selecting the Open as App logo returns to the Overview.',
  },
  '/help/screens/navigation#lists-in-the-menu': {
    title: 'Lists in the menu',
    page: 'Navigation and sidebar',
    text: "Subscriptions, Users, Apps and Leads have a small arrow on the right. Select it to show or hide the list's pages; the menu remembers which ones are open.",
  },
  '/help/screens/navigation#help-bulbs': {
    title: 'Help bulbs',
    page: 'Navigation and sidebar',
    text: 'A small yellow light bulb sits next to every page title, KPI tile and chart. It explains what you are looking at:',
  },
  '/help/screens/navigation#bottom-of-the-sidebar': {
    title: 'Bottom of the sidebar',
    page: 'Navigation and sidebar',
    text: 'The left menu with the report pages, saved views and their counts, Help, your profile and the data sources',
  },
  '/help/screens/navigation#data-in-the-menu': {
    title: 'Data in the menu',
    page: 'Navigation and sidebar',
    text: 'Data lists the source tables, by system. It is closed until you select the heading. Each of its three parts opens its overview page when you select its name, and shows or hides its tables with the arrow next to it:',
  },
  '/help/screens/navigation#tables': {
    title: 'Tables',
    page: 'Navigation and sidebar',
    text: 'Every table sorts by any column: select a header to sort by it, again to reverse the order.',
  },
  '/help/screens/global-filters': {
    title: 'Global filters',
    text: 'Date range, comparison, MRR or ARR, product offer and billing type, set once and applied across pages',
  },
  '/help/screens/global-filters#date-range': {
    title: 'Date range',
    page: 'Global filters',
    text: 'The range is a set of whole calendar months. The button shows its first and last month. _(MTD)_ means the range ends with the current month, which is month to date: its MRR is measured now, not at the end of the month.',
  },
  '/help/screens/global-filters#compare-to-previous-period': {
    title: 'Compare to previous period',
    page: 'Global filters',
    text: 'Compares each KPI with the period of the same length that ends just before the selected one. For January to June 2026, that is July to December 2025.',
  },
  '/help/screens/global-filters#mrr-or-arr': {
    title: 'MRR or ARR',
    page: 'Global filters',
    text: 'Switches money amounts in charts and tables between monthly recurring revenue (MRR) and annual recurring revenue (ARR = MRR × 12).',
  },
  '/help/screens/global-filters#product-offer': {
    title: 'Product offer',
    page: 'Global filters',
    text: 'Business, Non-Profit or Enterprise. Every KPI, chart and list is limited to accounts of that offer; users and apps follow the offer of their account. See Offer and billing type.',
  },
  '/help/screens/global-filters#billing-type': {
    title: 'Billing type',
    page: 'Global filters',
    text: "Stripe · yearly, Stripe · monthly or Enterprise contract. It works like the offer filter. Selecting a row in the Overview's billing mix sets this filter too; select it again to clear it.",
  },
  '/help/screens/global-filters#which-page-uses-which-filter': {
    title: 'Which page uses which filter',
    page: 'Global filters',
    text: 'The Users page shows only the offer and billing filters: its tiles and lists ignore the date range. The Totals page shows only the date range; its links into the lists clear the offer and billing filters, so a list shows the rows its total counts.',
  },
  '/help/screens/account-details': {
    title: 'Account details',
    text: 'The side panel that opens from any account row, with MRR, users, apps, a 24-month history and every revenue change',
  },
  '/help/screens/account-details#figures': {
    title: 'Figures',
    page: 'Account details',
    text: 'The side panel that opens from any account row, with MRR, users, apps, a 24-month history and every revenue change',
  },
  '/help/screens/account-details#mrr-last-24-months': {
    title: 'MRR, last 24 months',
    page: 'Account details',
    text: "A line of the account's MRR for each of the last 24 months, up to the current month.",
  },
  '/help/screens/account-details#details': {
    title: 'Details',
    page: 'Account details',
    text: 'The side panel that opens from any account row, with MRR, users, apps, a 24-month history and every revenue change',
  },
  '/help/screens/account-details#subscription-history': {
    title: 'Subscription history',
    page: 'Account details',
    text: 'Every MRR change of the account, newest first, with month, reason and amount:',
  },
  '/help/screens/data-freshness': {
    title: 'Data freshness',
    text: "How current the figures are, from the Stripe sync time, the product data date and the portal's refresh cycle",
  },
  '/help/screens/data-freshness#stripe-sync-time': {
    title: 'Stripe sync time',
    page: 'Data freshness',
    text: '_Synced …_ at the bottom of the sidebar is the most recent synchronisation of Stripe subscriptions into the warehouse (the latest LastSyncedAt in stripe.Subscriptions), in UTC. Revenue, subscriptions, movements and renewals are as current as this time.',
  },
  '/help/screens/data-freshness#product-data-date': {
    title: 'Product data date',
    page: 'Data freshness',
    text: 'Teams, team members, apps and licenses come from the production copies dbo.Teams, dbo.Users, dbo.TeamUsers, dbo.Resources and dbo.Subscriptions. Their date is the newest team creation time in dbo.Teams.',
  },
  '/help/screens/data-freshness#activity-date': {
    title: 'Activity date',
    page: 'Data freshness',
    text: 'The last use of a team member (_Last active_, the _Active · 30 days_ status, active users and the active user share) is recorded only in the api snapshot (api.TeamMembers.LastClientUsage); the production copies have no such column.',
  },
  '/help/screens/data-freshness#app-usage-date': {
    title: 'App usage date',
    page: 'Data freshness',
    text: 'App opens, sessions and users come from the Application Insights event tables (bi.AppUsageDaily, bi.AppUsageMonthly and bi.AppEvent). A separate loader fills them every 10 minutes, so they are current even when the product data is not.',
  },
  '/help/screens/data-freshness#portal-refresh': {
    title: 'Portal refresh',
    page: 'Data freshness',
    text: 'The server loads the warehouse data into memory and calculates every KPI from it. After 30 minutes it reloads in the background and keeps answering from the previous load meanwhile.',
  },
  '/help/screens/data-freshness#current-month': {
    title: 'Current month',
    page: 'Data freshness',
    text: 'Figures for the current month are month to date. An account\'s MRR counts the invoice lines that cover the Stripe sync time, not the current clock: a subscription that renewed after the last sync has no new invoice in the warehouse yet, and measuring it "now" would show it as churned until the next sync.',
  },
  '/help/screens/sign-in': {
    title: 'Signing in and sessions',
    text: 'Two-step email and password sign-in, how long a session lasts, signing out, and what is not available yet',
  },
  '/help/screens/sign-in#sign-in': {
    title: 'Sign in',
    page: 'Signing in and sessions',
    text: 'You then return to the page you originally opened, or to the Overview.',
  },
  '/help/screens/sign-in#sessions': {
    title: 'Sessions',
    page: 'Signing in and sessions',
    text: 'While you use the portal, the session renews itself in the background, so you are not asked to sign in again. How long it lasts without a visit depends on Keep me signed in:',
  },
  '/help/screens/sign-in#not-available-yet': {
    title: 'Not available yet',
    page: 'Signing in and sessions',
    text: 'Two-step email and password sign-in, how long a session lasts, signing out, and what is not available yet',
  },
  '/help/screens/overview': {
    title: 'Overview',
    text: 'Revenue, subscriptions and lifecycle at a glance, the starting page of the portal',
  },
  '/help/screens/overview#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Overview',
    text: 'Revenue, subscriptions and lifecycle at a glance, the starting page of the portal',
  },
  '/help/screens/overview#kpi-tiles': {
    title: 'KPI tiles',
    page: 'Overview',
    text: 'Eight tiles. With comparison on, each shows its change against the previous period; a change that rounds to zero (_±0.0%_) is grey. Select a tile to open the page behind it.',
  },
  '/help/screens/overview#month-to-date': {
    title: 'Month to date',
    page: 'Overview',
    text: 'When the range ends in the current month, that month is month to date (MTD): it holds the data up to the last Stripe sync. The ARR and MRR tiles say _MTD_, the charts label the month _Oct ’26 MTD_ and the readouts _Oct 2026 (MTD)_. Hover the date range to see the sync time.',
  },
  '/help/screens/overview#mrr-trend': {
    title: 'MRR trend',
    page: 'Overview',
    text: "The closing MRR of every month in the range (ARR trend when the unit is ARR). The subtitle gives the growth since the month before the range, in euros and percent, then the largest account's share of the period-end MRR and the MRR excluding it with its change over the same months (_largest account 22.9% · excluding it…",
  },
  '/help/screens/overview#billing-mix': {
    title: 'Billing mix',
    page: 'Overview',
    text: 'MRR at the end of the period by billing type: Stripe · yearly, Stripe · monthly and Enterprise contract, with the number of subscriptions, ARPA and share of each. Enterprise plans billed monthly through Stripe are in _Stripe · monthly_.',
  },
  '/help/screens/overview#mrr-movements': {
    title: 'MRR movements',
    page: 'Overview',
    text: 'One bar per month: new and expansion MRR above the line, contraction and churned MRR below it. The legend shows the hovered month (by default the last one) and its net change. Select a month to see the accounts behind it on the Revenue page.',
  },
  '/help/screens/overview#trial-funnel': {
    title: 'Trial funnel',
    page: 'Overview',
    text: 'Self-serve 30-day trials started in the period: started, converted to paid and expired. The funnel covers all trials, whatever the offer and billing filters. When the product data ends before the period does, the subtitle names its date.',
  },
  '/help/screens/overview#lifecycle-today': {
    title: 'Lifecycle today',
    page: 'Overview',
    text: 'How many accounts are in each lifecycle state today, whatever the date range (lifecycle counts).',
  },
  '/help/screens/overview#filters': {
    title: 'Filters',
    page: 'Overview',
    text: 'All global filters apply. _Lifecycle today_ and _Renewals due · 90 days_ ignore the date range: they are always measured today.',
  },
  '/help/screens/revenue': {
    title: 'Revenue',
    text: 'The MRR bridge month by month, the accounts behind every movement, and the payments collected, failed, refunded, disputed and paid out',
  },
  '/help/screens/revenue#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Revenue',
    text: 'The MRR bridge month by month, the accounts behind every movement, and the payments collected, failed, refunded, disputed and paid out',
  },
  '/help/screens/revenue#kpi-tiles': {
    title: 'KPI tiles',
    page: 'Revenue',
    text: 'With comparison on, the MRR tiles show the % change against the previous period; for contraction and churn, a decrease is green. Quick ratio shows the difference of the two ratios instead (for example _−1.42_), because it is already a ratio. A change that rounds to zero is grey.',
  },
  '/help/screens/revenue#mrr-bridge': {
    title: 'MRR bridge',
    page: 'Revenue',
    text: 'One row per month of the range, newest first. The current month is labelled _(MTD)_: month to date.',
  },
  '/help/screens/revenue#month-details': {
    title: 'Month details',
    page: 'Revenue',
    text: "Select a month in the bridge, or a month on the Overview's charts, to list its account movements on the right, largest first.",
  },
  '/help/screens/revenue#payments': {
    title: 'Payments',
    page: 'Revenue',
    text: 'Below the bridge, _Payments_ shows the money that actually moved: live-mode Stripe payments in euros, and invoices paid outside Stripe. The figures are as current as the Stripe sync; the page keeps them for up to 30 minutes, like the other portal data.',
  },
  '/help/screens/revenue#payments-per-month': {
    title: 'Payments per month',
    page: 'Revenue',
    text: 'One row per month of the range, newest first, with the same figures as the tiles, the failure rate and the money paid outside Stripe; the footer adds the period and, with comparison on, the previous period. Failed attempts include card retries that later succeeded. The current month is labelled _(MTD)_.',
  },
  '/help/screens/revenue#why-payments-failed': {
    title: 'Why payments failed',
    page: 'Revenue',
    text: 'The Stripe failure codes of the period, most frequent first, with their share of the failed attempts.',
  },
  '/help/screens/revenue#recent-failed-payments': {
    title: 'Recent failed payments',
    page: 'Revenue',
    text: "The 25 newest failed attempts of the period: date, subscription, amount in the charge's currency, failure code, Stripe's message and the charge. The subscription is the one of the charge's invoice when it is in the portal, otherwise the customer's subscription with the highest MRR, otherwise the Stripe customer.",
  },
  '/help/screens/revenue#filters': {
    title: 'Filters',
    page: 'Revenue',
    text: 'Date range, comparison, unit, offer and billing apply to the MRR tiles and the bridge. _Payments_ follows the date range and comparison only: payments belong to Stripe customers, not to one offer, and are always in euros, not MRR or ARR.',
  },
  '/help/screens/subscriptions': {
    title: 'Subscriptions',
    text: 'Every Open as App license and Stripe subscription, paying, trial, unbilled or ended, in lists you can search, filter, sort and save as views',
  },
  '/help/screens/subscriptions#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Subscriptions',
    text: 'Each row is an account: one Open as App license, or one Stripe subscription with recurring EUR revenue. A license paid by a Stripe subscription is one row, the Stripe subscription, with _Source_ "License + Stripe".',
  },
  '/help/screens/subscriptions#subscriptions-dashboard': {
    title: 'Subscriptions dashboard',
    page: 'Subscriptions',
    text: 'Subscriptions › Dashboard in the menu (/subscriptions/dashboard) charts every account in the offer and billing filters: tiles with the counts of the main views, subscriptions started per month, and breakdowns by lifecycle, offer, billing, source, country, renewal risk and license plan.',
  },
  '/help/screens/subscriptions#kpi-tiles': {
    title: 'KPI tiles',
    page: 'Subscriptions',
    text: 'Each tile counts the accounts of a predefined view, the same number as on the Data views page, and opens that view.',
  },
  '/help/screens/subscriptions#charts': {
    title: 'Charts',
    page: 'Subscriptions',
    text: 'The charts count accounts by their current fields, so they change with the offer and billing filters; only the per-month charts use the date range.',
  },
  '/help/screens/subscriptions#lifecycle-of-list-rows': {
    title: 'Lifecycle of list rows',
    page: 'Subscriptions',
    text: 'The _Lifecycle_ card here, the _Lifecycle_ cards of the views and _By lifecycle today_ on the Totals page count every row of the list: the Stripe subscriptions and every Open as App license: about 130,000 license rows, most of them from one bulk grant in February 2025, and every ended trial.',
  },
  '/help/screens/subscriptions#lifecycle-analysis': {
    title: 'Lifecycle analysis',
    page: 'Subscriptions',
    text: 'Subscriptions › Lifecycle in the menu (/subscriptions/lifecycle) follows the rows of the list through the lifecycle states: where they are today, how they moved through the states month by month, and what ended. The offer and billing filters apply to every chart; the date range applies to the per-month charts.',
  },
  '/help/screens/subscriptions#lifecycle-tiles': {
    title: 'Lifecycle tiles',
    page: 'Subscriptions',
    text: 'Each tile also shows the users and apps of its rows. The tabs below the tiles group the charts: Today, Over time, Ended & recovery, Archived and Deleted.',
  },
  '/help/screens/subscriptions#lifecycle-stages': {
    title: 'Lifecycle stages',
    page: 'Subscriptions',
    text: 'On the Today tab: a bar with every state but Archived and Deleted in lifecycle order, sized by its rows, and a table with one row per state: hint, rows, share, money, users and apps. Select a segment of the bar or a row to list that state.',
  },
  '/help/screens/subscriptions#lifecycle-by-offer-billing-and-source': {
    title: 'Lifecycle by offer, billing and source',
    page: 'Subscriptions',
    text: "On the Today tab: each offer, billing and source value with its rows, its MRR today and a bar of its share in each stage, to compare how far each group got. Select the group's name to list its rows (Archived and Deleted left out), or a segment of its bar to list the group in that stage.",
  },
  '/help/screens/subscriptions#started-per-month-by-stage-today': {
    title: 'Started per month, by stage today',
    page: 'Subscriptions',
    text: 'On the Over time tab: rows by the month they started, one column per month of the date range, split by their stage today. Read it as a cohort view: how many of the subscriptions started in a month are still running, and how many have ended. The switch above the chart selects the measure:',
  },
  '/help/screens/subscriptions#ended-per-month-by-what-ended': {
    title: 'Ended per month, by what ended',
    page: 'Subscriptions',
    text: 'On the Over time tab: rows by the month they ended, split by their billing: _Unbilled license_, _Trial_ (an expired test license) or _Stripe-billed_ (yearly, monthly or an enterprise contract). It shows the rows that ended within the last 20 days; earlier ones are Archived or Deleted and on their own tabs.',
  },
  '/help/screens/subscriptions#trials-and-paying-subscriptions': {
    title: 'Trials and paying subscriptions',
    page: 'Subscriptions',
    text: 'On the Over time tab:',
  },
  '/help/screens/subscriptions#recovery-window': {
    title: 'Recovery window',
    page: 'Subscriptions',
    text: 'On the Ended & recovery tab: the rows that ended within 20 days by time since they ended, which is what their state says: _Deactivated_ (days 1–10) and _Disabled_ (days 11–20), each with rows, share, lost MRR, users and apps. Both are still recoverable; rows that ended earlier are on the Archived and Deleted tabs.',
  },
  '/help/screens/subscriptions#ended-and-recoverable-by-group': {
    title: 'Ended and recoverable by group',
    page: 'Subscriptions',
    text: 'On the Ended & recovery tab, a band of compact cards breaks down the _Recoverable_ rows (Deactivated or Disabled) by offer, billing, source and country. The Archived and Deleted tabs have the same band for their rows. Select a row of a card to list those subscriptions.',
  },
  '/help/screens/subscriptions#archived-subscriptions': {
    title: 'Archived subscriptions',
    page: 'Subscriptions',
    text: 'The Archived tab covers the rows that ended 21 days to 6 months ago: past the recovery window, not yet deleted. It has the same tiles, month charts and group cards as the Deleted tab.',
  },
  '/help/screens/subscriptions#deleted-subscriptions': {
    title: 'Deleted subscriptions',
    page: 'Subscriptions',
    text: 'The Deleted tab covers the rows that ended more than 6 months ago:',
  },
  '/help/screens/subscriptions#churn-analysis': {
    title: 'Churn analysis',
    page: 'Subscriptions',
    text: "Subscriptions › Churn in the menu (/subscriptions/churn) answers how much is lost, when, by whom, how long customers stay, and what may be lost next. The offer and billing filters apply to every chart; the date range applies to everything except the survival curve and the _At risk_ tab, which are today's.",
  },
  '/help/screens/subscriptions#churn-tiles': {
    title: 'Churn tiles',
    page: 'Subscriptions',
    text: 'Every Open as App license and Stripe subscription, paying, trial, unbilled or ended, in lists you can search, filter, sort and save as views',
  },
  '/help/screens/subscriptions#scale-month-quarter-or-year': {
    title: 'Scale: month, quarter or year',
    page: 'Subscriptions',
    text: "The Over time tab has a _Month_ / _Quarter_ / _Year_ switch above its charts (_Month_ by default). It groups the range's months into calendar quarters or years for all three charts.",
  },
  '/help/screens/subscriptions#churn-rate-per-month': {
    title: 'Churn rate per month',
    page: 'Subscriptions',
    text: 'On the Over time tab, one column per month (or quarter or year):',
  },
  '/help/screens/subscriptions#churned-per-month-by-billing': {
    title: 'Churned per month, by billing',
    page: 'Subscriptions',
    text: 'Subscriptions by their churn month, split into monthly, yearly and enterprise contract billing, with the switch Count, Share or MRR (_MRR before churn_). Select a part of a column to list those subscriptions. A subscription that churned twice counts in the month of its last churn.',
  },
  '/help/screens/subscriptions#mrr-lost-per-month': {
    title: 'MRR lost per month',
    page: 'Subscriptions',
    text: 'Churned MRR (subscriptions that stopped paying) and contraction MRR (subscriptions that pay less) per month, from the MRR bridge.',
  },
  '/help/screens/subscriptions#who-churns': {
    title: 'Who churns',
    page: 'Subscriptions',
    text: 'The Who churns tab compares the subscriptions that churned in the range with those still paying today that first paid before the range ended, per offer, billing, MRR, team size, published apps, source and country:',
  },
  '/help/screens/subscriptions#largest-losses': {
    title: 'Largest losses',
    page: 'Subscriptions',
    text: 'The ten churned subscriptions of the range that paid the most before they stopped, each linking to its record, and a link to all churned subscriptions of the range.',
  },
  '/help/screens/subscriptions#survival-by-months-since-start': {
    title: 'Survival by months since start',
    page: 'Subscriptions',
    text: "On the Retention tab, the share of subscriptions still paying 0, 3, 6 … 21 months after their first paying month, over all start-quarter cohorts old enough for that month, weighted by their accounts (Cohort logo retention). _Revenue_ weights the cohorts' revenue retention by their start MRR.",
  },
  '/help/screens/subscriptions#churn-by-offer': {
    title: 'Churn by offer',
    page: 'Subscriptions',
    text: 'Logo churn per offer over the range and its trailing 12-month NRR, as on Product offers (Offer NRR and logo churn).',
  },
  '/help/screens/subscriptions#at-risk': {
    title: 'At risk',
    page: 'Subscriptions',
    text: 'The At risk tab shows what may churn next, as of today:',
  },
  '/help/screens/subscriptions#comparison': {
    title: 'Comparison',
    page: 'Subscriptions',
    text: 'Subscriptions › Comparison in the menu (/subscriptions/compare) puts two periods side by side: A, the period you look at (pink), against B, the baseline (grey). Every change reads as A against B: _Change_ is A − B, _Change %_ is that difference over B.',
  },
  '/help/screens/subscriptions#choosing-the-periods': {
    title: 'Choosing the periods',
    page: 'Subscriptions',
    text: 'The menu at the right of the header, next to the offer and billing filters, picks a ready-made pair, computed from the data date (it reads _Custom periods_ when the sides match none):',
  },
  '/help/screens/subscriptions#comparison-tabs': {
    title: 'Comparison tabs',
    page: 'Subscriptions',
    text: 'Tabs under the period pickers switch what the two periods are compared on; the periods stay. The tab is in the page address (?tab=lifecycle), so it can be bookmarked with the periods.',
  },
  '/help/screens/subscriptions#comparison-tiles': {
    title: 'Comparison tiles',
    page: 'Subscriptions',
    text: "Six tiles show A's value of the main figures: _Started_, _Started, paying today_, _First revenue_, _Ended_, _Churned_ and _Live at end_. Under each, the change against B in percent (green when it is good news: more starts, fewer endings) and B's value. The Measures table defines each figure.",
  },
  '/help/screens/subscriptions#measures': {
    title: 'Measures',
    page: 'Subscriptions',
    text: "Every figure, counted on both sides, with the change; Export saves the table with both periods' dates.",
  },
  '/help/screens/subscriptions#per-step-chart': {
    title: 'Per step chart',
    page: 'Subscriptions',
    text: "The chosen measure per day (periods up to 31 days), week (up to 93 days) or month, A's step _n_ next to B's step _n_. Running total adds the steps up, which shows when one side pulled ahead. Hover a pair for its dates and counts.",
  },
  '/help/screens/subscriptions#breakdown-by-field': {
    title: 'Breakdown by field',
    page: 'Subscriptions',
    text: "The rows a measure counts on each side, grouped by offer, billing, license plan, source, country or lifecycle today, with a bar of A and B per value. Select a count to open _All subscriptions_ narrowed to that side's period and value, as unsaved changes.",
  },
  '/help/screens/subscriptions#lifecycle-comparison-tiles': {
    title: 'Lifecycle comparison tiles',
    page: 'Subscriptions',
    text: '_Live at start_, _Started_, _Ended_ and _Live at end_ as in Measures; _Net change of live base_, live at end minus live at start; and _Started, live today_: of the subscriptions started in the period, those whose lifecycle state today is Active or Grace.',
  },
  '/help/screens/subscriptions#live-base-bridge': {
    title: 'Live base bridge',
    page: 'Subscriptions',
    text: "Live at start, plus started, minus ended, gives live at end, for both sides: A's bar above B's, on one scale. When the steps don't add up exactly (rows without a start date, or ended before they started), a line under the chart says by how much.",
  },
  '/help/screens/subscriptions#started-subscriptions-today': {
    title: 'Started subscriptions today',
    page: 'Subscriptions',
    text: "The subscriptions each side started, grouped by their lifecycle today or billing today, as counts and as shares of the side's total. Shares make periods of different sizes comparable: _Share change_ is A's share minus B's, in percentage points. Select a count to list those subscriptions.",
  },
  '/help/screens/subscriptions#live-at-end-by-field': {
    title: 'Live at end by field',
    page: 'Subscriptions',
    text: "The live base on each side's last day, grouped by today's offer, billing, license plan, source, country or lifecycle, as counts and shares. Fields describe the rows today, so a subscription live at the end of an earlier period shows its current billing or state.",
  },
  '/help/screens/subscriptions#churn-vs-new-tiles': {
    title: 'Churn vs New tiles',
    page: 'Subscriptions',
    text: "_New subscriptions_ and _Lost subscriptions_ are _Started_ and _Ended_ of Measures; _Net subscriptions_ is their difference. _New MRR_, _Churned MRR_ and _Net new MRR_ come from the MRR bridge of each side's whole months, as on the Revenue page; they show — when a side has no whole month.",
  },
  '/help/screens/subscriptions#new-and-lost-per-step': {
    title: 'New and lost per step',
    page: 'Subscriptions',
    text: 'One chart per side, on one scale: subscriptions started above the line and ended below it, per day, week or month as in the per step chart. The head of each chart reads out the totals, or the hovered step.',
  },
  '/help/screens/subscriptions#mrr-movement': {
    title: 'MRR movement',
    page: 'Subscriptions',
    text: "The MRR bridge of each side's whole months: opening MRR, new, expansion, contraction and churned MRR, net new MRR and closing MRR, then new, lost and net paying subscriptions. The column headers name the months each side counts. Amounts are MRR; the ARR switch of other pages does not apply. Export saves the table.",
  },
  '/help/screens/subscriptions#new-and-lost-by-field': {
    title: 'New and lost by field',
    page: 'Subscriptions',
    text: "Each side's started and ended subscriptions, and their net, grouped by today's offer, billing, license plan, source, country or lifecycle; _Net change_ is A's net minus B's. Select a count to list those subscriptions.",
  },
  '/help/screens/subscriptions#view-dashboards': {
    title: 'View dashboards',
    page: 'Subscriptions',
    text: "Between a view's title and its list, compact cards chart the accounts of that view: _In this view_ (the count, its share of all accounts, and a link to the dashboard), then three breakdowns chosen for the view. They skip what the view already fixes: _Trials ending soon_ shows no lifecycle card.",
  },
  '/help/screens/subscriptions#data-views': {
    title: 'Data views',
    page: 'Subscriptions',
    text: 'Subscriptions › Data views in the menu (/subscriptions/views) shows each predefined view below, and each of your own views, as a tile: its name, how many accounts it matches with the current offer and billing filters, and its description. Select a tile to open the list in that view.',
  },
  '/help/screens/subscriptions#predefined-views': {
    title: 'Predefined views',
    page: 'Subscriptions',
    text: 'Every Open as App license and Stripe subscription, paying, trial, unbilled or ended, in lists you can search, filter, sort and save as views',
  },
  '/help/screens/subscriptions#columns': {
    title: 'Columns',
    page: 'Subscriptions',
    text: 'Every field of an account can be a column; see Account fields. Money columns follow the MRR or ARR switch. Select a column header to sort by it. Two columns are KPIs of their own: Active user share and At user limit.',
  },
  '/help/screens/subscriptions#working-with-the-list': {
    title: 'Working with the list',
    page: 'Subscriptions',
    text: 'Every Open as App license and Stripe subscription, paying, trial, unbilled or ended, in lists you can search, filter, sort and save as views',
  },
  '/help/screens/subscriptions#links-into-this-list': {
    title: 'Links into this list',
    page: 'Subscriptions',
    text: 'Other pages open this list with extra filters, which start as unsaved changes of _All subscriptions_, so the list holds the accounts the tile or row counts:',
  },
  '/help/screens/subscriptions#filters': {
    title: 'Filters',
    page: 'Subscriptions',
    text: "Offer and billing apply. The date range does not: the list always shows the accounts' current state. Only the _Started per month_ and _Ended per month_ charts use the date range.",
  },
  '/help/screens/renewals': {
    title: 'Renewals',
    text: 'Yearly plans and enterprise contracts renewing in the next 12 months, with risk flags and a renewal forecast',
  },
  '/help/screens/renewals#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Renewals',
    text: 'Only yearly Stripe plans and enterprise contracts renew once a year. Monthly plans are summarised in one tile.',
  },
  '/help/screens/renewals#kpi-tiles': {
    title: 'KPI tiles',
    page: 'Renewals',
    text: 'An overdue renewal is due already: an unpaid renewal (Grace) is dated when its unpaid period started, and a renewal whose period ended before the last Stripe sync is still listed. Both count in every horizon.',
  },
  '/help/screens/renewals#renewal-value-by-month': {
    title: 'Renewal value by month',
    page: 'Renewals',
    text: "One column per month from the current month to the month 365 days ahead (13 columns when that month is not complete yet): the renewal value due in that month, split into healthy and at risk. Overdue renewals are in the current month's column, so the columns add up to _Due in 12 months_.",
  },
  '/help/screens/renewals#due-in-the-next-90-days': {
    title: 'Due in the next 90 days',
    page: 'Renewals',
    text: 'Every renewal due in the next 90 days, overdue ones included, with its date and days to go (_Overdue 197 days_ for one that is due already), account, offer, billing, renewal value, active users out of all users, and risk. Select At risk to show only renewals with a risk. Select a row to open the account details.',
  },
  '/help/screens/renewals#filters': {
    title: 'Filters',
    page: 'Renewals',
    text: 'Offer and billing apply. The date range does not: renewals always count from today. Renewal values are annual and do not follow the MRR or ARR switch.',
  },
  '/help/screens/product-offers': {
    title: 'Product offers',
    text: 'Revenue, growth and retention per offer, additional user seats, the Stripe price catalogue (standard, custom and archived offers), discounts and coupons',
  },
  '/help/screens/product-offers#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Product offers',
    text: 'Product offers has five subpages in the menu: Standard, Custom and Archived offers, Discounts and Coupons.',
  },
  '/help/screens/product-offers#product-offers-table': {
    title: 'Product offers table',
    page: 'Product offers',
    text: "One row per offer, for the last month of the date range. Select a row to list the offer's paying subscriptions (_Paying_ = yes: MRR today).",
  },
  '/help/screens/product-offers#plan-customizations': {
    title: 'Plan customizations',
    page: 'Product offers',
    text: 'Add-ons on top of base plans. Today the only add-on is the additional user seat. The subtitle shows how many paying self-serve (Business and Non-Profit) subscriptions have at least one.',
  },
  '/help/screens/product-offers#price-catalogue': {
    title: 'Price catalogue',
    page: 'Product offers',
    text: "Every live Stripe product, split into standard, custom and archived offers, with its number of products, prices, live subscriptions and list MRR. Select a row to open that page. Unlike the tables above, the catalogue is today's: the date range does not apply.",
  },
  '/help/screens/product-offers#standard-offers': {
    title: 'Standard offers',
    page: 'Product offers',
    text: 'The Standard page lists the core products: active Stripe products linked to at least 5 subscriptions. These are the base offers Business, Non-Profit and Enterprise, their add-ons such as additional user seats, and the free plan.',
  },
  '/help/screens/product-offers#custom-offers': {
    title: 'Custom offers',
    page: 'Product offers',
    text: 'The Custom page lists the other active products: made for one customer or a deal (1 to 4 linked subscriptions), or never used. The _Why_ column gives the number of linked subscriptions, or _Never used_. Same tiles and tabs as the Standard page.',
  },
  '/help/screens/product-offers#archived-offers': {
    title: 'Archived offers',
    page: 'Product offers',
    text: 'The Archived page lists the products that are archived in Stripe or have no active price. They can no longer be sold, but existing subscriptions keep paying them: the _Live subscriptions_ tile shows how many still do.',
  },
  '/help/screens/product-offers#offer-breakdown': {
    title: 'Offer breakdown',
    page: 'Product offers',
    text: 'The Overview tab of the Standard, Custom and Archived pages shows four charts of their prices. Select a bar or row to open the Prices tab with only those prices.',
  },
  '/help/screens/product-offers#prices-table': {
    title: 'Prices table',
    page: 'Product offers',
    text: 'The Prices tab lists one row per Stripe price, most live subscriptions first. Select a price to open it in the data explorer. _All_, _In use_ and _Not in use_ filter on live subscriptions.',
  },
  '/help/screens/product-offers#products-table': {
    title: 'Products table',
    page: 'Product offers',
    text: 'The Products tab lists one row per Stripe product, largest list value first. Select a product to open it in the data explorer.',
  },
  '/help/screens/product-offers#discounts': {
    title: 'Discounts',
    page: 'Product offers',
    text: 'The Discounts page shows how much revenue discounts cost and how discounted customers behave. See Discount, coupon and promotion code.',
  },
  '/help/screens/product-offers#coupons': {
    title: 'Coupons',
    page: 'Product offers',
    text: 'The Coupons page lists the Stripe coupons and the promotion codes customers enter, with their lifetime redemptions. See Discount, coupon and promotion code.',
  },
  '/help/screens/product-offers#filters': {
    title: 'Filters',
    page: 'Product offers',
    text: "The end month of the range sets the snapshot; the whole range is used for _New in period_ and _Logo churn_. Unit, offer and billing apply. With an offer filter, only that offer's row is shown.",
  },
  '/help/screens/users': {
    title: 'Users',
    text: "Every member of every Open as App team, with activity, roles, open invitations and the team's subscription",
  },
  '/help/screens/users#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Users',
    text: "Every member of every Open as App team, with activity, roles, open invitations and the team's subscription",
  },
  '/help/screens/users#where-users-come-from': {
    title: 'Where users come from',
    page: 'Users',
    text: 'A user is a member of an Open as App team: its owner (dbo.Teams.CreatorId) and the people invited to it (dbo.TeamUsers), from the production copies that are current to the product data date; every team counts, also teams without any license or Stripe subscription.',
  },
  '/help/screens/users#kpi-tiles': {
    title: 'KPI tiles',
    page: 'Users',
    text: "The tiles count users of paying subscriptions (Active or Grace), whatever view is selected below. Each tile opens the predefined view with the same users, so the tile and the view's count on the Data views page are equal.",
  },
  '/help/screens/users#data-views': {
    title: 'Data views',
    page: 'Users',
    text: 'Users › Data views in the menu (/users/views) shows the views below, and your own, as tiles with their counts; select one to open it. The filter bar narrows every count by the year, quarter or month the user was added, the role and the status. See Data views pages.',
  },
  '/help/screens/users#predefined-views': {
    title: 'Predefined views',
    page: 'Users',
    text: 'The views that match a tile are limited to paying subscriptions, like the tiles. For the same users of every team, open _All users_ and filter by status, role or _Added_ with Customize.',
  },
  '/help/screens/users#filters': {
    title: 'Filters',
    page: 'Users',
    text: "Offer and billing apply through each user's subscription; with one of them set, members of teams without a subscription are left out. The date range, compare and MRR/ARR do not apply, so the page shows only the offer and billing filters.",
  },
  '/help/screens/apps': {
    title: 'Apps',
    text: 'Apps that customers build on Open as App, how many are published, public, connected or new, and how much they are used',
  },
  '/help/screens/apps#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Apps',
    text: 'Apps that customers build on Open as App, how many are published, public, connected or new, and how much they are used',
  },
  '/help/screens/apps#where-apps-come-from': {
    title: 'Where apps come from',
    page: 'Apps',
    text: "Every app in Open as App (api.Apps), also apps of teams without any license or Stripe subscription. Each app shows its team's main subscription, or the subscription state _None_. See Team.",
  },
  '/help/screens/apps#apps-dashboard': {
    title: 'Apps dashboard',
    page: 'Apps',
    text: 'Apps › Dashboard in the menu (/apps/dashboard) shows the figures of every app in the global filters. The list pages show the apps of one view, each with its own compact dashboard (below).',
  },
  '/help/screens/apps#kpi-tiles': {
    title: 'KPI tiles',
    page: 'Apps',
    text: 'Select a tile to open the matching view of the list. The app list is a snapshot: the _Apps_ tile names its date ("as of 24 Mar 2026"), and apps created after it are not counted anywhere on this part of the dashboard.',
  },
  '/help/screens/apps#charts': {
    title: 'Charts',
    page: 'Apps',
    text: 'Apps that customers build on Open as App, how many are published, public, connected or new, and how much they are used',
  },
  '/help/screens/apps#usage': {
    title: 'Usage',
    page: 'Apps',
    text: 'Below the charts, the dashboard shows how the apps are used, from the event tables.',
  },
  '/help/screens/apps#view-dashboards': {
    title: 'View dashboards',
    page: 'Apps',
    text: "Between a view's title and its list, compact cards describe the apps of that view: _In this view_ (the count and its share of all apps), then three breakdowns chosen for the view. They skip what the view already fixes: _Published_ shows no status card.",
  },
  '/help/screens/apps#data-views': {
    title: 'Data views',
    page: 'Apps',
    text: 'Apps › Data views in the menu (/apps/views) shows the views below, and your own, as tiles with their counts; select one to open it. The filter bar narrows every count by the year, quarter or month the app was created, its type, status and data source. See Data views pages.',
  },
  '/help/screens/apps#predefined-views': {
    title: 'Predefined views',
    page: 'Apps',
    text: "Search covers app name, subscription, and the creator's name and email. Each field is described in App, including the usage columns _Last used_, _Opens · 30d_, _Users · 30d_, _Sessions · 30d_, _Active days · 30d_ and _Platforms · 30d_. Add them to any view with Customize. Select a row to open the app's record page.",
  },
  '/help/screens/apps#filters': {
    title: 'Filters',
    page: 'Apps',
    text: "Offer and billing apply through the app's subscription; with one of them set, apps of teams without a subscription are left out, and so are the events of apps missing from the app list.",
  },
  '/help/screens/cohorts': {
    title: 'Cohorts',
    text: 'Revenue and logo retention of subscriptions grouped by the quarter they started paying',
  },
  '/help/screens/cohorts#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Cohorts',
    text: 'Revenue and logo retention of subscriptions grouped by the quarter they started paying',
  },
  '/help/screens/cohorts#the-heatmap': {
    title: 'The heatmap',
    page: 'Cohorts',
    text: 'One row per quarter for the last eight complete quarters; the current quarter is not shown.',
  },
  '/help/screens/cohorts#how-to-read-it': {
    title: 'How to read it',
    page: 'Cohorts',
    text: 'Revenue and logo retention of subscriptions grouped by the quarter they started paying',
  },
  '/help/screens/cohorts#filters': {
    title: 'Filters',
    page: 'Cohorts',
    text: 'Offer, billing and the MRR or ARR unit (for Start MRR) apply. The date range does not.',
  },
  '/help/screens/totals': {
    title: 'Totals',
    text: 'Every row of every source table, how many were added in the period, and what the Subscriptions list is made of',
  },
  '/help/screens/totals#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Totals',
    text: 'Totals count every row, whatever the offer and billing filters: they equal SELECT COUNT() on the source table.',
  },
  '/help/screens/totals#kpi-tiles': {
    title: 'KPI tiles',
    page: 'Totals',
    text: 'One tile per series, with its total, how many rows were added in the date range, and the source table. Select a tile to open the list of exactly those rows, or its table in the data explorer when no list holds them. The links clear the offer and billing filters (Totals ignore them), so the list count equals the total:',
  },
  '/help/screens/totals#over-time': {
    title: 'Over time',
    page: 'Totals',
    text: 'A chart of the selected series: its cumulative total and the rows added per month in the date range. Choose another series in the chart or the table. Series from the api snapshot (only with Portal:ProductSource = api) stop at the product data date, and the subtitle says so.',
  },
  '/help/screens/totals#subscriptions-list': {
    title: 'Subscriptions list',
    page: 'Totals',
    text: 'The rows of the Subscriptions list by source: _License_ (license without Stripe billing), _License + Stripe_ (license paired with its Stripe subscription) and _Stripe only_ (Stripe subscription without a license row), with their share.',
  },
  '/help/screens/totals#all-source-tables': {
    title: 'All source tables',
    page: 'Totals',
    text: 'Every series with its total, the rows created before the range, the rows added in the range, and rows without a creation date. When the range ends before the newest rows (a range in the past), an _After_ column holds the rows created after it, so before + added + after + no date = total.',
  },
  '/help/screens/totals#filters': {
    title: 'Filters',
    page: 'Totals',
    text: 'Only the date range applies; offer and billing do not.',
  },
  '/help/screens/trends': {
    title: 'Trends',
    text: 'Where revenue, customers, acquisition and product usage are heading, month by month, and which metrics are improving or worsening',
  },
  '/help/screens/trends#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Trends',
    text: "Trends brings together the monthly series of the other reports. It reads them from the same data, so a month's value here equals the value on the report it links to.",
  },
  '/help/screens/trends#kpi-tiles': {
    title: 'KPI tiles',
    page: 'Trends',
    text: 'Six headline trends: MRR (or ARR), net new MRR, paying subscriptions, sign-ups, trials started and app opens. Each tile shows the latest complete month and its momentum (see Signals). Select a tile to open its perspective.',
  },
  '/help/screens/trends#signals': {
    title: 'Signals',
    page: 'Trends',
    text: 'The first tab ranks the metrics by momentum:',
  },
  '/help/screens/trends#all-trends': {
    title: 'All trends',
    page: 'Trends',
    text: "Every metric in one table: its shape over the range, the latest complete month and its value, the 3-month average, the momentum, the trend per month (the least-squares slope of the range's complete months, relative to their mean) and the best month (the lowest for metrics where lower is better). Every column sorts.",
  },
  '/help/screens/trends#perspectives': {
    title: 'Perspectives',
    page: 'Trends',
    text: "Each perspective tab has a card per metric: a chart of the range, the metric's signal, its trend per month and its best month, and a link to the report it comes from. Hover over or focus a month to read its value. The bulb of a card opens its definition below, then the KPI topics.",
  },
  '/help/screens/trends#revenue': {
    title: 'Revenue',
    page: 'Trends',
    text: 'Where revenue, customers, acquisition and product usage are heading, month by month, and which metrics are improving or worsening',
  },
  '/help/screens/trends#customers': {
    title: 'Customers',
    page: 'Trends',
    text: "Monthly logo churn is measured per month. The logo churn KPI measures a period's starting base instead, so the two differ.",
  },
  '/help/screens/trends#acquisition': {
    title: 'Acquisition',
    page: 'Trends',
    text: 'Where revenue, customers, acquisition and product usage are heading, month by month, and which metrics are improving or worsening',
  },
  '/help/screens/trends#product-usage': {
    title: 'Product usage',
    page: 'Trends',
    text: 'App usage comes from the event tables: a month without loaded events is a gap, a partly loaded month is faded and left out of the signal (see Data freshness).',
  },
  '/help/screens/trends#lenses': {
    title: 'Lenses',
    page: 'Trends',
    text: 'The switch above the cards changes what the charts show:',
  },
  '/help/screens/trends#filters-and-data': {
    title: 'Filters and data',
    page: 'Trends',
    text: 'The date range sets the months shown. The page also loads the 12 months before the range for averages, momentum and year-on-year changes. Revenue and customer metrics from the MRR series follow the offer and billing filters. Trials follow the offer filter (the trial plan).',
  },
  '/help/screens/subscription-reports': {
    title: 'Subscription reports',
    text: 'Management reports on revenue, churn, renewals, trials and offers, each viewable on the page and exportable as a PDF',
  },
  '/help/screens/subscription-reports#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Subscription reports',
    text: 'Subscriptions › Reports in the menu (/subscriptions/reports) lists the management reports. Each tile shows what the report covers, the period it will use, and its sections. Select View report to read it on the page, or Export PDF to open it and save it as a PDF at once.',
  },
  '/help/screens/subscription-reports#period-and-filters': {
    title: 'Period and filters',
    page: 'Subscription reports',
    text: 'Reports use the global filters above the page: the date range sets the period, Offer and Billing the subscriptions they cover. Every report compares the period with the period of the same length before it, whether or not _Compare_ is switched on, as long as the data reaches back that far.',
  },
  '/help/screens/subscription-reports#export-pdf': {
    title: 'Export PDF',
    page: 'Subscription reports',
    text: "Export PDF opens the browser's print dialog with the report laid out for A4 paper: light colours, without the menu, the filters and the buttons. Choose Save as PDF as the printer (Chrome, Edge: _Destination › Save as PDF_; Safari: _PDF › Save as PDF_).",
  },
  '/help/screens/subscription-reports#executive-summary': {
    title: 'Executive summary',
    page: 'Subscription reports',
    text: 'Revenue, customers and retention of the period on one page: ARR, MRR, paying subscriptions, ARPA, net revenue retention, logo churn, gross revenue churn and renewals due in 90 days, each with its change against the previous period.',
  },
  '/help/screens/subscription-reports#mrr-movements': {
    title: 'MRR movements',
    page: 'Subscription reports',
    text: "Where MRR came from and where it went: the totals of new, expansion, contraction and churned MRR, net new MRR and the quick ratio; the bridge of every month with new and lost subscriptions; and the 15 largest account movements of the period's last month. The same figures as the Revenue page.",
  },
  '/help/screens/subscription-reports#churn-and-retention': {
    title: 'Churn and retention',
    page: 'Subscription reports',
    text: "Logo churn, gross revenue churn, net revenue retention, lost subscriptions and churned MRR; churned MRR per month; the accounts lost in the period's last month; and the revenue and logo retention of each start quarter 3, 6 and 12 months later, as on the Cohorts page. Cohort retention ignores the date range.",
  },
  '/help/screens/subscription-reports#renewals-outlook': {
    title: 'Renewals outlook',
    page: 'Subscription reports',
    text: 'Looks ahead from today, so the date range does not apply. The renewal value due in 90 days and in 12 months, the part at risk, and the monthly plans; renewal value by month; every renewal at risk within 12 months; and the 15 largest renewals due in 90 days.',
  },
  '/help/screens/subscription-reports#trials-and-conversion': {
    title: 'Trials and conversion',
    page: 'Subscription reports',
    text: 'Trials started in the period and what became of them: started, converted, the final conversion rate of trials old enough to have converted, the MRR they brought and how many built an app.',
  },
  '/help/screens/subscription-reports#plans-and-offers': {
    title: 'Plans and offers',
    page: 'Subscription reports',
    text: 'MRR, paying subscriptions, self-serve and customized subscriptions and add-on MRR at the end of the period; per offer its subscriptions, MRR and share, add-on share, ARPA, new subscriptions in the period, retention and average users; and the add-ons with their attach rate. The same figures as the Product offers page.',
  },
  '/help/screens/saved-views': {
    title: 'Saved views and Customize',
    text: 'Filter, sort and choose columns in the Subscriptions, Users and Apps lists, and save the result as your own view',
  },
  '/help/screens/saved-views#customize': {
    title: 'Customize',
    page: 'Saved views and Customize',
    text: 'Customize opens a panel with three parts:',
  },
  '/help/screens/saved-views#operators': {
    title: 'Operators',
    page: 'Saved views and Customize',
    text: 'Text matching ignores upper and lower case. Day-based date filters count from today for subscriptions, and from the product data date for users and apps. _In the last 30 days_ is today and the 29 days before: 30 days, like the 30-day tiles.',
  },
  '/help/screens/saved-views#save': {
    title: 'Save',
    page: 'Saved views and Customize',
    text: 'Filter, sort and choose columns in the Subscriptions, Users and Apps lists, and save the result as your own view',
  },
  '/help/screens/saved-views#where-views-are-kept': {
    title: 'Where views are kept',
    page: 'Saved views and Customize',
    text: "Your views are stored in this browser only. They are not shared with colleagues and do not follow you to another browser or computer. Clearing the browser's site data removes them.",
  },
  '/help/screens/saved-views#data-views-pages': {
    title: 'Data views pages',
    page: 'Saved views and Customize',
    text: "Each list has a Data views page in its menu: /subscriptions/views, /users/views, /apps/views and /leads/views. It shows each predefined view, and under My views each of your own, as a tile: the view's name, how many rows it matches and its description. Select a tile to open the list in that view.",
  },
  '/help/screens/data-explorer': {
    title: 'Data explorer',
    text: 'Browse, sort and filter every source table behind the portal, and follow links between records',
  },
  '/help/screens/data-explorer#schemas': {
    title: 'Schemas',
    page: 'Data explorer',
    text: 'Browse, sort and filter every source table behind the portal, and follow links between records',
  },
  '/help/screens/data-explorer#source-overview': {
    title: 'Source overview',
    page: 'Data explorer',
    text: "OAA, Stripe and BI in the menu each open an overview of their tables (/data/source/oaa, /data/source/stripe, /data/source/bi). The tiles count tables and views, all records of the tables (views repeat their tables' rows and are not added), the largest table, fields, the portal's joins and the KPIs that read the…",
  },
  '/help/screens/data-explorer#table-overview': {
    title: 'Table overview',
    page: 'Data explorer',
    text: 'A table in the menu opens its page (/data/stripe/charges), the only page of the table. The tiles show Records, Last 30 days and Newest record (dated tables), fields, relations and KPIs. Under them are the same tabs as on a source overview, with Data after Overview and Fields in place of Tables:',
  },
  '/help/screens/data-explorer#totals': {
    title: 'Totals',
    page: 'Data explorer',
    text: 'The tiles at the top of a table page count its rows and stay above every tab:',
  },
  '/help/screens/data-explorer#views': {
    title: 'Views',
    page: 'Data explorer',
    text: 'Each table has views: saved sets of filters and a sort. The View list at the top of the Data tab picks one; the Views card of the Overview tab lists them with their row counts. The menu lists only the table.',
  },
  '/help/screens/data-explorer#table-pages': {
    title: 'Table pages',
    page: 'Data explorer',
    text: '_Does not equal_ keeps the rows where the column is empty: Country _does not equal_ DE also lists the customers without a country. To leave them out, add _is not empty_. The other conditions (equals, greater than, contains, …) never match an empty value.',
  },
  '/help/screens/data-explorer#charts-view': {
    title: 'Charts view',
    page: 'Data explorer',
    text: "Charts view above a table's records shows charts in place of the rows, for the rows the current view and filters match. Table view brings the rows back. The page address does not change, so a shared link still opens the table.",
  },
  '/help/screens/data-explorer#records-and-links': {
    title: 'Records and links',
    page: 'Data explorer',
    text: 'Double-click a row (or select it and press Enter) to open its form:',
  },
  '/help/screens/data-explorer#values': {
    title: 'Values',
    page: 'Data explorer',
    text: 'Browse, sort and filter every source table behind the portal, and follow links between records',
  },
  '/help/screens/data-explorer#performance': {
    title: 'Performance',
    page: 'Data explorer',
    text: 'Some tables are large (dbo.AppUsage has more than 12 million rows), and the api tables have no indexes. Filtering, sorting and counting them can take up to two minutes.',
  },
  '/help/screens/profile': {
    title: 'Profile',
    text: 'Your portal account, default revenue unit and date range, notification switches and sign-out',
  },
  '/help/screens/profile#account': {
    title: 'Account',
    page: 'Profile',
    text: "Your name and email, the date your portal account was created, your browser's time zone, and until when you stay signed in without a visit; every visit moves this date on (see Signing in and sessions). Sign out ends the session on this device, in all tabs.",
  },
  '/help/screens/profile#sign-in-methods': {
    title: 'Sign-in methods',
    page: 'Profile',
    text: 'Email and password is the only method; connecting a Microsoft account is not available yet, so its button is disabled.',
  },
  '/help/screens/profile#preferences': {
    title: 'Preferences',
    page: 'Profile',
    text: 'Changing the revenue unit or the default range resets the global filters (range, comparison, offer and billing) to the new defaults. Preferences are stored in this browser.',
  },
  '/help/screens/profile#active-sessions': {
    title: 'Active sessions',
    page: 'Profile',
    text: 'Every browser signed in to your account: the device, when it signed in and when it was last used. The current browser is marked This device. Sessions without _Keep me signed in_ say that they end when the browser closes.',
  },
  '/help/screens/record-pages': {
    title: 'Record pages',
    text: 'One page per subscription, user or app, with all its fields and its related records in tabs',
  },
  '/help/screens/record-pages#tabs': {
    title: 'Tabs',
    page: 'Record pages',
    text: 'A record page has a Main tab with every field, and tabs for related records. Each tab shows how many records it holds; the selected tab is part of the page address, so you can share it.',
  },
  '/help/screens/record-pages#app-use-last-24-months': {
    title: 'App use, last 24 months',
    page: 'Record pages',
    text: "On the subscription record's main tab, below the MRR history, a chart shows the opens of the team's apps per month, for the same 24 months. The filled foot of a bar is the opens in the mobile apps; a faded bar is a month that is only partly loaded, and a month without loaded events shows no bar.",
  },
  '/help/screens/record-pages#app-bi-tab': {
    title: 'App BI tab',
    page: 'Record pages',
    text: 'The BI tab of an app record charts everything linked to the app, from the app events and the earlier usage tables.',
  },
  '/help/screens/record-pages#open-as-app-and-stripe-links': {
    title: 'Open as App and Stripe links',
    page: 'Record pages',
    text: 'The record pages connect a portal record with the current production copy of Open as App and with Stripe:',
  },
  '/help/screens/record-pages#ids': {
    title: 'IDs',
    page: 'Record pages',
    text: "A subscription's ID is its Stripe subscription ID (sub_…) or, for a license without Stripe billing, lic- followed by the team ID. A user's ID is the team ID and the email, because team members have no ID of their own.",
  },
  '/help/screens/conversion': {
    title: 'Conversion',
    text: 'The Stripe Checkout funnel, who opens the payment page, who buys and who drops out',
  },
  '/help/screens/conversion#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Conversion',
    text: 'The Stripe Checkout funnel, who opens the payment page, who buys and who drops out',
  },
  '/help/screens/conversion#kpi-tiles': {
    title: 'KPI tiles',
    page: 'Conversion',
    text: 'With comparison on, each tile shows the change against the previous period; for Abandoned, a decrease is green.',
  },
  '/help/screens/conversion#charts-and-tables': {
    title: 'Charts and tables',
    page: 'Conversion',
    text: 'Test sessions from development and staging hosts are left out, and the page says how many. It also gives the time of the latest checkout session in the data.',
  },
  '/help/screens/conversion#checkout-visitors-per-month': {
    title: 'Checkout visitors per month',
    page: 'Conversion',
    text: "Visitors per month, with the buyers as the filled part of each bar. For ranges of up to 12 months, the label on a bar is that month's conversion.",
  },
  '/help/screens/conversion#conversion-by-checkout-amount': {
    title: 'Conversion by checkout amount',
    page: 'Conversion',
    text: 'The most frequent amounts in the period, with the matching Stripe plan, sessions, visitors, buyers and conversion. The plan is the live Stripe price with that amount that most subscriptions use.',
  },
  '/help/screens/conversion#where-checkouts-start': {
    title: 'Where checkouts start',
    page: 'Conversion',
    text: 'Sessions by entry point (portal checkout, payment link, HubSpot quote, other) and the buyers they produced.',
  },
  '/help/screens/conversion#time-to-buy': {
    title: 'Time to buy',
    page: 'Conversion',
    text: 'For buyers in the period, the time from their first checkout session (at most 90 days earlier) to the purchase: within 1 hour, 1–24 hours, 1–7 days, over 7 days. See Time to buy.',
  },
  '/help/screens/conversion#filters': {
    title: 'Filters',
    page: 'Conversion',
    text: 'Only the date range and comparison apply. Offer and billing do not, because checkout happens before a subscription exists.',
  },
  '/help/screens/trials': {
    title: 'Trials',
    text: 'What becomes of the 30-day test licenses: how many convert to paid each month, to which plan, and how much the trials use their apps',
  },
  '/help/screens/trials#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Trials',
    text: 'What becomes of the 30-day test licenses: how many convert to paid each month, to which plan, and how much the trials use their apps',
  },
  '/help/screens/trials#what-counts-as-a-trial': {
    title: 'What counts as a trial',
    page: 'Trials',
    text: 'A trial is a self-serve test license: an Open as App license lasting 1–31 days, or 32–62 days without a license count (the 60-day trials of mid-2025), the same rule as the Trials started KPI. The February 2025 bulk grant of licenses is not a trial.',
  },
  '/help/screens/trials#trial-tiles': {
    title: 'Trial tiles',
    page: 'Trials',
    text: 'What becomes of the 30-day test licenses: how many convert to paid each month, to which plan, and how much the trials use their apps',
  },
  '/help/screens/trials#outcomes': {
    title: 'Outcomes',
    page: 'Trials',
    text: 'What becomes of the 30-day test licenses: how many convert to paid each month, to which plan, and how much the trials use their apps',
  },
  '/help/screens/trials#what-became-of-each-month-s-trials': {
    title: "What became of each month's trials",
    page: 'Trials',
    text: 'One column per start month, split into _Converted to paid_, _Still running_ and _Expired_ (ended without converting so far). Share draws each month at full height. Trials of the last 90 days can still convert, so their _Expired_ part can still turn into _Converted_; the subtitle names the first such month.',
  },
  '/help/screens/trials#conversion-rate-per-start-month': {
    title: 'Conversion rate per start month',
    page: 'Trials',
    text: "Converted trials as a share of each month's trials. Months whose trials can still convert are faded.",
  },
  '/help/screens/trials#converted-to': {
    title: 'Converted to',
    page: 'Trials',
    text: 'What becomes of the 30-day test licenses: how many convert to paid each month, to which plan, and how much the trials use their apps',
  },
  '/help/screens/trials#plans-they-converted-to': {
    title: 'Plans they converted to',
    page: 'Trials',
    text: 'The converted trials by the offer and billing of the Stripe subscription they pay, with its MRR in the first paying month and how many still pay today. A subscription the portal leaves out (not in euros, or never paid) is grouped as _Not in the portal_.',
  },
  '/help/screens/trials#conversion-by-trial-plan': {
    title: 'Conversion by trial plan',
    page: 'Trials',
    text: 'Each trial plan with its trials and conversion rate.',
  },
  '/help/screens/trials#usage-during-trials': {
    title: 'Usage during trials',
    page: 'Trials',
    text: 'What becomes of the 30-day test licenses: how many convert to paid each month, to which plan, and how much the trials use their apps',
  },
  '/help/screens/trials#built-and-used-apps': {
    title: 'Built and used apps',
    page: 'Trials',
    text: 'Per start month, the share of trials that created an app during the trial, and the share of measured trials that used their apps (any app usage recorded on the trial license). Months without usage data are faded.',
  },
  '/help/screens/trials#conversion-by-usage': {
    title: 'Conversion by usage',
    page: 'Trials',
    text: 'Measured trials in five tiers, each with its trials and conversion rate: _No app built, not used_, _Built an app, not used_, and used on 1–2, 3–6 or 7+ days. It shows whether the trials that use their apps convert more often.',
  },
  '/help/screens/trials#usage-data': {
    title: 'Usage data',
    page: 'Trials',
    text: 'App usage comes from the app event tables, recorded per day on the license the app belongs to. It has gaps: there is no usage data from April to November 2025, and December 2025 is incomplete.',
  },
  '/help/screens/markets': {
    title: 'Markets',
    text: 'Payments received by customer country and region, paying customers and new customers per market',
  },
  '/help/screens/markets#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Markets',
    text: 'Payments received by customer country and region, paying customers and new customers per market',
  },
  '/help/screens/markets#kpi-tiles': {
    title: 'KPI tiles',
    page: 'Markets',
    text: 'When the range ends in the current month, that month is month to date: a note under the tiles says so, the previous period is cut at the same point, and the monthly chart labels the month _MTD_ with a lighter bar.',
  },
  '/help/screens/markets#charts-and-tables': {
    title: 'Charts and tables',
    page: 'Markets',
    text: "A customer's country is the card country of their latest payment, otherwise the Stripe or checkout billing address; see Payment.",
  },
  '/help/screens/markets#payments-by-region': {
    title: 'Payments by region',
    page: 'Markets',
    text: 'The share of payments per region, with the change against the previous period.',
  },
  '/help/screens/markets#countries': {
    title: 'Countries',
    page: 'Markets',
    text: 'Every country with payments, region, share, change against the previous period, paying customers and new customers.',
  },
  '/help/screens/markets#payments-received-per-month': {
    title: 'Payments received per month',
    page: 'Markets',
    text: 'Payments received per month, all markets, gross euros. Payments in other currencies are converted to euros through Stripe; money without a euro value is left out and listed in the subtitle.',
  },
  '/help/screens/markets#filters': {
    title: 'Filters',
    page: 'Markets',
    text: 'Only the date range and comparison apply; offer and billing do not.',
  },
  '/help/screens/markets#good-to-know': {
    title: 'Good to know',
    page: 'Markets',
    text: 'Payments are cash, not MRR: a yearly plan pays twelve months at once, so a month with many yearly renewals looks large. Use Revenue for recurring revenue.',
  },
  '/help/screens/leads': {
    title: 'Leads',
    text: 'People and teams with buying intent who have never paid, ranked by intent score, in lists you can filter and save as views',
  },
  '/help/screens/leads#questions-it-answers': {
    title: 'Questions it answers',
    page: 'Leads',
    text: 'Each row is a lead: an email or team that has never paid. Leads are ranked by their intent score, and the _Signals_ column says why.',
  },
  '/help/screens/leads#data-views': {
    title: 'Data views',
    page: 'Leads',
    text: 'Leads › Data views in the menu (/leads/views) shows the views below, and your own, as tiles with their counts; select one to open it. The filter bar narrows every count by the year, quarter or month the lead signed up, the lead type, the stage and the number of apps. See Data views pages.',
  },
  '/help/screens/leads#predefined-views': {
    title: 'Predefined views',
    page: 'Leads',
    text: 'Search covers lead name, email and domain. Customizing and saving work as on the other lists; see Saved views. Lead rows have no record page.',
  },
  '/help/screens/leads#filters': {
    title: 'Filters',
    page: 'Leads',
    text: 'No global filters apply: leads are not tied to a period, offer or billing type.',
  },
  '/help/kpis/mrr': {
    title: 'MRR',
    text: 'Monthly recurring revenue, what subscriptions bill per month, with yearly invoices spread evenly over their months',
  },
  '/help/kpis/arr': {
    title: 'ARR',
    text: 'Annual recurring revenue, the yearly run rate of the recurring business (MRR × 12)',
  },
  '/help/kpis/arpa': {
    title: 'ARPA',
    text: 'Average revenue per account, the MRR of an average paying subscription per month',
  },
  '/help/kpis/net-new-mrr': {
    title: 'Net new MRR',
    text: 'How much MRR changed in a month or period, everything added minus everything lost',
  },
  '/help/kpis/mrr-growth': {
    title: 'MRR growth rate',
    text: 'Net new MRR as a percentage of the MRR at the start of the month or period',
  },
  '/help/kpis/new-mrr': {
    title: 'New MRR',
    text: 'MRR from subscriptions that start paying, new customers and reactivated subscriptions',
  },
  '/help/kpis/expansion-mrr': {
    title: 'Expansion MRR',
    text: 'Additional MRR from subscriptions that already paid, through more seats, upgrades or higher prices',
  },
  '/help/kpis/contraction-mrr': {
    title: 'Contraction MRR',
    text: 'MRR lost by subscriptions that keep paying, through fewer seats, downgrades or lower prices',
  },
  '/help/kpis/churned-mrr': {
    title: 'Churned MRR',
    text: 'MRR lost from subscriptions that stopped paying',
  },
  '/help/kpis/quick-ratio': {
    title: 'Quick ratio',
    text: 'How much MRR is gained for every euro of MRR lost',
  },
  '/help/kpis/billing-mix': {
    title: 'Billing mix',
    text: 'The split of MRR between yearly Stripe plans, monthly Stripe plans and enterprise contracts',
  },
  '/help/kpis/paying-subscriptions': {
    title: 'Paying subscriptions',
    text: 'The number of accounts with recurring revenue at the end of the period',
  },
  '/help/kpis/new-subscriptions': {
    title: 'New subscriptions',
    text: 'The number of subscriptions that started paying, or paid again, in the period',
  },
  '/help/kpis/lost-subscriptions': {
    title: 'Lost subscriptions',
    text: 'The number of subscriptions that stopped paying in the period',
  },
  '/help/kpis/lifecycle-counts': {
    title: 'Lifecycle today',
    text: 'How many accounts are in each lifecycle state today, with their current or recoverable MRR',
  },
  '/help/kpis/recoverable-mrr': {
    title: 'Recoverable MRR',
    text: 'The MRR that recently ended subscriptions paid before they churned, and could pay again if won back',
  },
  '/help/kpis/net-revenue-retention': {
    title: 'Net revenue retention',
    text: 'How much of the revenue of customers from 12 months ago you still have today, including their upgrades and downgrades',
  },
  '/help/kpis/logo-churn': {
    title: 'Logo churn',
    text: 'The share of the customers paying at the start of the period that no longer pay at its end',
  },
  '/help/kpis/gross-revenue-churn': {
    title: 'Gross revenue churn',
    text: 'The share of the MRR of the customers paying at the start of the period that they no longer pay at its end, before any expansion',
  },
  '/help/kpis/cohort-revenue-retention': {
    title: 'Cohort revenue retention',
    text: 'The MRR of a start-quarter cohort after 0, 3, 6 … 21 months, compared with its MRR at the start',
  },
  '/help/kpis/cohort-logo-retention': {
    title: 'Cohort logo retention',
    text: "The share of a start-quarter cohort's subscriptions still paying after 0, 3, 6 … 21 months",
  },
  '/help/kpis/renewal-value': {
    title: 'Renewal value',
    text: 'The annual value of a yearly plan or contract at its next renewal (current MRR × 12)',
  },
  '/help/kpis/renewals-due-90-days': {
    title: 'Renewals due in 90 days',
    text: 'The annual value of yearly plans and contracts renewing in the next 90 days, plus the overdue unpaid renewals',
  },
  '/help/kpis/renewals-at-risk': {
    title: 'Renewals at risk',
    text: 'Renewals due in the next 90 days or overdue that are unpaid, set to cancel, not billed or with low usage, and the rules behind each flag',
  },
  '/help/kpis/renewals-due-12-months': {
    title: 'Renewals due in 12 months',
    text: 'The annual value of all yearly plans and contracts renewing within the next 365 days',
  },
  '/help/kpis/billed-monthly': {
    title: 'Billed monthly',
    text: 'The current MRR and number of subscriptions on monthly billing, which renew every month',
  },
  '/help/kpis/offer-subscriptions-and-share': {
    title: 'Offer subscriptions, MRR and share',
    text: 'How many paying subscriptions and how much MRR each offer has at the end month, and its share of the total',
  },
  '/help/kpis/offer-new-in-period': {
    title: 'New in period by offer',
    text: 'How many customers of each offer had their first month with revenue for the offer in the date range',
  },
  '/help/kpis/offer-retention': {
    title: 'Offer NRR and logo churn',
    text: 'Net revenue retention over 12 months and the share of the customers paying at the start that were lost in the period, per offer',
  },
  '/help/kpis/add-on-share': {
    title: 'Add-on share',
    text: "The part of an offer's MRR that comes from additional user seats",
  },
  '/help/kpis/add-on-attach-rate': {
    title: 'Add-on attach rate',
    text: 'The share of paying self-serve subscriptions with at least one additional user seat, the seat revenue and the typical seat price',
  },
  '/help/kpis/average-users': {
    title: 'Average users per subscription',
    text: "The average number of team members on an offer's paying subscriptions",
  },
  '/help/kpis/offer-price': {
    title: 'Offer price',
    text: 'The typical monthly base price of an offer, the median that paying customers actually pay',
  },
  '/help/kpis/offer-kind': {
    title: 'Standard, custom and archived offers',
    text: 'How each Stripe product is sorted into a standard offer (a core product), a custom offer (customer-specific, one-off or unused) or an archived offer, by its number of linked subscriptions',
  },
  '/help/kpis/list-mrr': {
    title: 'List MRR',
    text: 'The monthly value of the live subscriptions on a price at its list amount, before discounts',
  },
  '/help/kpis/trials-started': {
    title: 'Trials started',
    text: 'The number of trial (test) licenses that started in the months of the date range',
  },
  '/help/kpis/trial-conversion': {
    title: 'Trial conversion',
    text: 'How many trials led to a Stripe subscription within 90 days, and the conversion rate',
  },
  '/help/kpis/expired-trials': {
    title: 'Expired trials',
    text: 'Trials started in the period that neither converted nor are still running',
  },
  '/help/kpis/running-trials': {
    title: 'Running trials',
    text: 'Trial (test) licenses that have not ended yet',
  },
  '/help/kpis/users-on-paying-subscriptions': {
    title: 'Users on paying subscriptions',
    text: 'Team members of all Active and Grace subscriptions, and the average per subscription',
  },
  '/help/kpis/active-users': {
    title: 'Active users',
    text: 'Users on paying subscriptions who used Open as App in the 30 days up to the activity date (the api snapshot of 24 Mar 2026)',
  },
  '/help/kpis/invited-not-joined': {
    title: 'Invited, not joined',
    text: 'Users on paying subscriptions who were invited to a team but have not accepted the invitation',
  },
  '/help/kpis/owners-and-admins': {
    title: 'Owners and admins',
    text: 'Users on paying subscriptions with the team role Owner or Admin',
  },
  '/help/kpis/users-added-30-days': {
    title: 'Users added in 30 days',
    text: 'Users on paying subscriptions who were added to their team in the 30 days up to the product data date',
  },
  '/help/kpis/active-user-share': {
    title: 'Active user share',
    text: 'Per subscription, the share of its team members who were active in the last 30 days of product data',
  },
  '/help/kpis/at-user-limit': {
    title: 'At user limit',
    text: 'Paying subscriptions whose team uses every licensed seat',
  },
  '/help/kpis/total-apps': {
    title: 'Apps',
    text: 'All Open as App apps, or the apps of the subscriptions in the filters, and how many are drafts',
  },
  '/help/kpis/published-apps': {
    title: 'Published apps',
    text: 'Apps that are published, publicly or privately, and their share of all apps',
  },
  '/help/kpis/published-in-period': {
    title: 'Published in period',
    text: 'Published apps created in the months of the date range, compared with the previous period',
  },
  '/help/kpis/public-apps': {
    title: 'Public apps',
    text: 'Published apps that anyone can open without signing in, and their share of published apps',
  },
  '/help/kpis/connected-apps': {
    title: 'Connected to databases or APIs',
    text: 'Apps whose data source is an SQL database or a REST API',
  },
  '/help/kpis/apps-created-30-days': {
    title: 'Apps created in 30 days',
    text: 'Apps created in the 30 days before the product data date, drafts included',
  },
  '/help/kpis/apps-by-data-source': {
    title: 'Apps by data source',
    text: 'How many apps use each data source, from spreadsheets and uploads to databases and APIs',
  },
  '/help/kpis/active-apps': {
    title: 'Active apps · 30 days',
    text: 'Apps used on at least one of the last 30 complete days, and the change against the 30 days before',
  },
  '/help/kpis/app-opens': {
    title: 'App opens and sessions · 30 days',
    text: 'How often apps were opened, and in how many sessions, in the last 30 days, with the change against the 30 days before',
  },
  '/help/kpis/published-apps-not-used': {
    title: 'Published apps not used · 30 days',
    text: 'Published apps without any app events in the last 30 days, and their share of published apps',
  },
  '/help/kpis/app-opens-per-month': {
    title: 'App opens per month',
    text: 'App opens per month of the date range, the share in the mobile apps, opens by platform, and which months are fully loaded',
  },
  '/help/kpis/checkout-visitors': {
    title: 'Checkout visitors',
    text: 'The number of people who opened Stripe Checkout, the payment page, in the period',
  },
  '/help/kpis/buyers': {
    title: 'Buyers',
    text: 'Checkout visitors who completed a purchase in the period',
  },
  '/help/kpis/checkout-conversion': {
    title: 'Checkout conversion',
    text: 'The share of checkout visitors who bought',
  },
  '/help/kpis/abandoned-checkouts': {
    title: 'Abandoned checkouts',
    text: 'Checkout visitors who did not buy within 14 days of their last visit, once those 14 days have passed',
  },
  '/help/kpis/new-paying-customers': {
    title: 'New paying customers',
    text: 'People (Stripe customers, one per email) whose first paid invoice falls in the period',
  },
  '/help/kpis/time-to-buy': {
    title: 'Time to buy',
    text: 'How long buyers take from their first checkout visit to the purchase',
  },
  '/help/kpis/payments-received': {
    title: 'Payments received',
    text: 'Money received in the period, gross, in euros: succeeded Stripe payments plus invoices paid outside Stripe (bank transfer)',
  },
  '/help/kpis/paying-customers-by-market': {
    title: 'Paying customers by market',
    text: 'Stripe customers with at least one payment in the period (Stripe or bank transfer), and how many countries they are in',
  },
  '/help/kpis/largest-market': {
    title: 'Largest market',
    text: 'The country that paid the most in the period, and its share of all payments',
  },
  '/help/kpis/lead-intent-score': {
    title: 'Lead intent score',
    text: 'A 0–100 score of how likely a lead is to pay, from checkout recency, email domain, apps, team size and trial',
  },
  '/help/kpis/discount-share': {
    title: 'Discount share of gross billings',
    text: 'The part of what customers would have paid at full price that discounts took away',
  },
  '/help/kpis/discounts-given': {
    title: 'Discounts given',
    text: 'The euro amount of discounts on paid invoices in the period, with the discounted invoices and customers',
  },
  '/help/kpis/discounted-vs-full-price': {
    title: 'Discounted vs full-price subscriptions',
    text: 'Paying subscriptions, MRR, monthly discount, NRR and logo churn of discounted subscriptions compared with full-price ones',
  },
  '/help/kpis/coupon-redemptions': {
    title: 'Coupon and promotion code redemptions',
    text: 'How often each Stripe coupon and promotion code has been redeemed, over its whole life',
  },
  '/help/kpis/row-totals': {
    title: 'Row totals',
    text: 'The number of rows of each source table, and how many were added per month',
  },
  '/help/kpis/payments-collected': {
    title: 'Payments collected',
    text: 'Money collected from succeeded Stripe payments in euros (other currencies converted), per month and in the period, next to the invoices paid outside Stripe',
  },
  '/help/kpis/failed-payment-attempts': {
    title: 'Failed payment attempts',
    text: 'Stripe charges that failed in the period, their share of all attempts, and why they failed',
  },
  '/help/kpis/refunded': {
    title: 'Refunded',
    text: 'Money paid back to customers through succeeded Stripe refunds in euros',
  },
  '/help/kpis/disputes': {
    title: 'Disputes',
    text: 'Chargebacks opened by card holders in the period, their amount, and how many were lost',
  },
  '/help/kpis/paid-out': {
    title: 'Paid out',
    text: 'Money Stripe transferred to the bank account, by arrival date',
  },
  '/help/data-model/account': {
    title: 'Account',
    text: 'One Stripe subscription with recurring revenue, or one Open as App license without Stripe billing; the rows of Subscriptions',
  },
  '/help/data-model/trial': {
    title: 'Trial',
    text: 'A test license of an Open as App team, shown as an account with billing Trial',
  },
  '/help/data-model/monthly-mrr': {
    title: 'Monthly MRR',
    text: "Each account's MRR for every calendar month, the series all revenue KPIs are added up from",
  },
  '/help/data-model/mrr-movement': {
    title: 'MRR movement',
    text: "A change in one account's MRR from one month to the next, classified as new, expansion, contraction or churn",
  },
  '/help/data-model/lifecycle-state': {
    title: 'Lifecycle state',
    text: 'Where an account is in its life, from active through grace to the stages after it ended',
  },
  '/help/data-model/offer-and-billing': {
    title: 'Offer and billing type',
    text: "How an account's offer (Business, Non-Profit, Enterprise) and billing type (yearly, monthly, contract) are determined",
  },
  '/help/data-model/add-on': {
    title: 'Add-on (additional user seat)',
    text: 'Extra user seats bought on top of a base plan, recognised by product or price name',
  },
  '/help/data-model/team': {
    title: 'Team',
    text: 'An Open as App team with its licenses, Stripe subscriptions, users and apps',
  },
  '/help/data-model/user': {
    title: 'User',
    text: 'A member of a linked Open as App team, with role, status and activity dates',
  },
  '/help/data-model/app': {
    title: 'App',
    text: 'An app built on a linked Open as App team, with type, status, visibility, data source, dates and recent use',
  },
  '/help/data-model/renewal': {
    title: 'Renewal',
    text: 'The next or overdue renewal of a paying yearly plan or enterprise contract, with its value and risk',
  },
  '/help/data-model/cohort': {
    title: 'Cohort',
    text: 'The customers (replacement chains of accounts) whose first month with revenue lies in the same calendar quarter',
  },
  '/help/data-model/period': {
    title: 'Reporting period',
    text: 'How the date range, the opening month, the previous period and the current month are defined',
  },
  '/help/data-model/saved-view': {
    title: 'Saved view',
    text: 'A named setup of a list with filters, sort and columns, predefined or saved in your browser',
  },
  '/help/data-model/license': {
    title: 'License',
    text: 'One Open as App license of a team, a row of api.Subscriptions, such as a trial, a bulk grant, a manual contract or the license behind a Stripe subscription',
  },
  '/help/data-model/checkout-session': {
    title: 'Checkout session',
    text: 'One visit to Stripe Checkout, the payment page, completed or abandoned, with amount, email, country and entry point',
  },
  '/help/data-model/payment': {
    title: 'Payment',
    text: "Money a customer paid: a succeeded Stripe charge or an invoice paid outside Stripe, in euros, attributed to the customer's country and market region",
  },
  '/help/data-model/lead': {
    title: 'Lead',
    text: 'A person or team that shows buying intent but has never paid, with stage, lead type and intent score',
  },
  '/help/data-model/discount': {
    title: 'Discount, coupon and promotion code',
    text: 'Discounts on paid invoices and on subscriptions, and the Stripe coupons and promotion codes behind them',
  },
  '/help/data-model/api-teams': {
    title: 'api.Teams',
    text: 'Open as App teams (workspaces) with their email, the link between Stripe customers and product data',
  },
  '/help/data-model/api-team-members': {
    title: 'api.TeamMembers',
    text: 'Members of Open as App teams with their role, invitation status and last activity',
  },
  '/help/data-model/api-apps': {
    title: 'api.Apps',
    text: 'Apps built in Open as App, with publish mode, data source, template and dates',
  },
  '/help/data-model/api-creators': {
    title: 'api.Creators',
    text: 'Open as App user accounts, with name, email, language and since when they create apps',
  },
  '/help/data-model/api-subscriptions': {
    title: 'api.Subscriptions',
    text: 'History of Open as App licenses per team, including the test licenses used for trials',
  },
  '/help/data-model/api-active-subscriptions': {
    title: 'api.ActiveSubscriptions',
    text: 'The Open as App license currently active for each team, with its number of licensed users',
  },
  '/help/data-model/api-team-features': {
    title: 'api.TeamFeatures',
    text: 'Features switched on for individual teams',
  },
  '/help/data-model/api-app-edits': {
    title: 'api.AppEdits',
    text: 'A log of when apps were edited',
  },
  '/help/data-model/api-app-invitations': {
    title: 'api.AppInvitations',
    text: 'Invitations of people to individual apps, with role and acceptance',
  },
  '/help/data-model/api-hubs': {
    title: 'api.Hubs',
    text: 'App hubs, collections of apps published together for a team',
  },
  '/help/data-model/api-hub-apps': {
    title: 'api.HubApps',
    text: 'Which apps belong to which hub',
  },
  '/help/data-model/dbo-users': {
    title: 'dbo.Users',
    text: 'Every Open as App user account, with sign-up date, flags and marketing fields',
  },
  '/help/data-model/dbo-teams': {
    title: 'dbo.Teams',
    text: 'Every Open as App team, with its creator and name',
  },
  '/help/data-model/dbo-team-users': {
    title: 'dbo.TeamUsers',
    text: 'The people invited to a team, with their role and when they accepted',
  },
  '/help/data-model/dbo-subscriptions': {
    title: 'dbo.Subscriptions',
    text: 'Every Open as App license, with plan, licenses, trial flags, status and its Stripe subscription',
  },
  '/help/data-model/dbo-subscription-plans': {
    title: 'dbo.SubscriptionPlans',
    text: 'The Open as App plans that licenses refer to',
  },
  '/help/data-model/dbo-resources': {
    title: 'dbo.Resources',
    text: 'Open as App apps and other resources, with settings, team, creator and copy origin',
  },
  '/help/data-model/stripe-customers': {
    title: 'stripe.Customers',
    text: 'Billing customers with email, name, address and balance',
  },
  '/help/data-model/stripe-subscriptions': {
    title: 'stripe.Subscriptions',
    text: 'Stripe subscriptions with status, billing period, cancellation and trial dates; each one with revenue is a portal account',
  },
  '/help/data-model/stripe-subscription-items': {
    title: 'stripe.SubscriptionItems',
    text: 'The prices and quantities a subscription currently consists of',
  },
  '/help/data-model/stripe-prices': {
    title: 'stripe.Prices',
    text: 'Prices of Stripe products, with amount, interval and the Open as App plan key',
  },
  '/help/data-model/stripe-products': {
    title: 'stripe.Products',
    text: 'Products sold through Stripe, such as plans and additional seats',
  },
  '/help/data-model/stripe-invoices': {
    title: 'stripe.Invoices',
    text: 'Invoices with status, totals, due and payment dates',
  },
  '/help/data-model/stripe-invoice-lines': {
    title: 'stripe.InvoiceLines',
    text: 'The lines of each invoice, with amount, discount, quantity and service period; the source of MRR',
  },
  '/help/data-model/stripe-invoice-custom-fields': {
    title: 'stripe.InvoiceCustomFields',
    text: 'Custom fields printed on invoices, such as purchase order numbers',
  },
  '/help/data-model/stripe-coupons': {
    title: 'stripe.Coupons',
    text: 'Discount definitions, as a percentage or an amount, for a duration',
  },
  '/help/data-model/stripe-discounts': {
    title: 'stripe.Discounts',
    text: 'Coupons applied to customers, subscriptions or invoices',
  },
  '/help/data-model/stripe-tax-rates': {
    title: 'stripe.TaxRates',
    text: 'Tax rates applied to invoices, by country and jurisdiction',
  },
  '/help/data-model/stripe-payment-intents': {
    title: 'stripe.PaymentIntents',
    text: 'Payment processes, from the request to collect money to its success or cancellation',
  },
  '/help/data-model/stripe-charges': {
    title: 'stripe.Charges',
    text: 'Attempts to charge a card or other payment method, successful or failed',
  },
  '/help/data-model/stripe-refunds': {
    title: 'stripe.Refunds',
    text: 'Money returned to customers for a charge',
  },
  '/help/data-model/stripe-disputes': {
    title: 'stripe.Disputes',
    text: 'Chargebacks raised by card holders, with reason, status and evidence deadlines',
  },
  '/help/data-model/stripe-balance-transactions': {
    title: 'stripe.BalanceTransactions',
    text: 'Every movement in the Stripe balance, with gross amount, fees and net amount',
  },
  '/help/data-model/stripe-payouts': {
    title: 'stripe.Payouts',
    text: 'Transfers from the Stripe balance to the bank account',
  },
  '/help/data-model/stripe-events': {
    title: 'stripe.Events',
    text: 'Stripe events (webhooks), such as created or updated subscriptions, with their payload',
  },
  '/help/data-model/stripe-objects': {
    title: 'stripe.Objects',
    text: 'Other Stripe objects without a table of their own, kept as JSON',
  },
  '/help/data-model/dbo-app-usage': {
    title: 'dbo.AppUsage',
    text: 'App opens per app, period, anonymised user, operating system, country and locale',
  },
  '/help/data-model/dbo-native-usage-last30-days': {
    title: 'dbo.NativeUsageLast30Days',
    text: 'View with opens and users per app in the last 30 days of native app usage',
  },
  '/help/data-model/dbo-web-client-usage-30-days': {
    title: 'dbo.WebClientUsage_30Days',
    text: 'Web client users, sessions and session length per app and platform in the last 30 days',
  },
  '/help/data-model/dbo-web-client-usage-total': {
    title: 'dbo.WebClientUsage_Total',
    text: 'Web client users, sessions and session length per app and platform, all time',
  },
  '/help/data-model/dbo-app-sizes': {
    title: 'dbo.AppSizes',
    text: 'Data size of apps per device over time',
  },
  '/help/data-model/dbo-app-features': {
    title: 'dbo.AppFeatures',
    text: 'Which features each app uses, and how often',
  },
  '/help/data-model/biapp-users': {
    title: 'biapp.Users',
    text: 'People who can sign in to the KPI portal, with name and email',
  },
  '/help/data-model/biapp-user-credentials': {
    title: 'biapp.UserCredentials',
    text: 'Sign-in credentials of portal accounts; the password hash is never shown',
  },
  '/help/data-model/bi-app-event': {
    title: 'bi.AppEvent',
    text: 'One row per product event from Application Insights, with event type, app, user, session, device and location',
  },
  '/help/data-model/bi-event-type': {
    title: 'bi.EventType',
    text: 'The names and categories of the product event types in bi.AppEvent',
  },
  '/help/data-model/bi-app-usage-daily': {
    title: 'bi.AppUsageDaily',
    text: 'App use per day, source, app, user, subscription, platform and country, counted by event category',
  },
  '/help/data-model/bi-app-usage-monthly': {
    title: 'bi.AppUsageMonthly',
    text: 'App use per month, source, app, user, subscription and platform, counted by event category, with active days',
  },
  '/help/data-model/bi-preview-interaction-daily': {
    title: 'bi.PreviewInteractionDaily',
    text: 'Interactions of viewers with public and embedded apps per day, including anonymous viewers',
  },
  '/help/data-model/bi-app-catalog': {
    title: 'bi.AppCatalog',
    text: 'The apps seen in the event data, with their name and when they first and last appeared',
  },
  '/help/data-model/bi-vw-app-event': {
    title: 'bi.vw_AppEvent',
    text: 'The app events with readable source, event name, category and app name',
  },
  '/help/data-model/bi-vw-app-usage-daily': {
    title: 'bi.vw_AppUsageDaily',
    text: 'Daily app use with the source and app names',
  },
  '/help/data-model/bi-vw-app-usage-monthly': {
    title: 'bi.vw_AppUsageMonthly',
    text: 'Monthly app use with the source and app names',
  },
  '/help/data-model/bi-vw-preview-interaction-daily': {
    title: 'bi.vw_PreviewInteractionDaily',
    text: 'Daily interactions with public and embedded apps, with the source and app names',
  },
  '/help/data-model/etl-source': {
    title: 'etl.Source',
    text: 'The seven Application Insights resources the event loader reads',
  },
  '/help/data-model/etl-watermark': {
    title: 'etl.Watermark',
    text: 'How far each loader job has loaded each source',
  },
  '/help/data-model/etl-run-log': {
    title: 'etl.RunLog',
    text: 'One row per run of a loader job, with its window, rows and status',
  },
  '/help/data-model/etl-dirty-day': {
    title: 'etl.DirtyDay',
    text: 'Days whose app usage must be aggregated again',
  },
  '/help/data-model/etl-event-category-rule': {
    title: 'etl.EventCategoryRule',
    text: 'Name patterns that sort the events into the categories counted in app usage',
  },
  '/help/data-model/etl-vw-load-status': {
    title: 'etl.vw_LoadStatus',
    text: 'One row per source: how far its events and previews are loaded and how the last run ended',
  },
  '/help/ai/access-and-data-protection': {
    title: 'Access and data protection',
    text: 'Which login an AI may use, which data must stay protected, and the read-only access IT sets up',
  },
  '/help/ai/portal-api': {
    title: 'Portal API',
    text: "Let an AI agent read the portal's own figures through its API, signed in with your portal account",
  },
  '/help/ai/sql-mcp-server': {
    title: 'SQL MCP Server',
    text: "Connect Claude, GitHub Copilot or another MCP client to chosen warehouse tables, read-only, with Microsoft's SQL MCP Server",
  },
  '/help/ai/vs-code-copilot': {
    title: 'VS Code and GitHub Copilot',
    text: 'Let GitHub Copilot write and run SQL against the warehouse in VS Code, with the MSSQL extension',
  },
  '/help/ai/context-for-ai': {
    title: 'Briefing for the AI',
    text: "A ready-made briefing on the warehouse's schemas, joins and pitfalls to give an AI assistant before you ask",
  },
  '/help/ai/example-questions': {
    title: 'Example questions and checking answers',
    text: 'Questions that work well, how to phrase them, and how to check an AI answer against the portal',
  },
};
